import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {Alert, Link} from "@mui/material";
import {Add, Apartment, Check, DeleteOutline, EditOutlined, Hub, Paid} from "@mui/icons-material";
import {useSitStores, useStores} from "~/stores";
import type {Cluster, Company, CompanyInput, Country, Currency} from "../../../ApiTypes";
import {SitRole} from "../../../ApiTypes";
import {SitField, SitSection} from "../../../components/design/SitDesignAtoms";
import {scrollToSettings} from "../settingsSections";

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October',
    'November', 'December'];

type Draft = CompanyInput & {id?: number};
const emptyDraft = (clusterId = 0, currencyId = 0, countryId = 0): Draft =>
    ({code: '', title: '', fiscal_year_start_month: 1, cluster_id: clusterId, currency_id: currencyId, country_id: countryId});

const errorText = (e: unknown): string => {
    const data = (e as {response?: {data?: unknown}})?.response?.data;
    if (data && typeof data === 'object') {
        const values = Object.values(data as Record<string, unknown>).flat();
        if (values.length) return values.map(String).join(' ');
    }
    return 'Could not save. Try again.';
};

const usedBy = (n: number | null | undefined, what: string) => n ? `${n} ${what}` : 'Not used';

/**
 * Settings sections Currencies, Clusters and Companies: what initiative financials are split across (companies, grouped in clusters) and the
 * currencies they are entered in. Only SIT admins change them; a row in use cannot be deleted.
 */
export const CompanySettings: React.FC = observer(() => {
    const {authStore} = useStores();
    const {ccStore, sitApi} = useSitStores();
    const isAdmin = authStore.sitUserProfile?.role === SitRole.ADMIN;
    const companies: Company[] = ccStore.companyRM.result ?? [];
    const clusters: Cluster[] = ccStore.clusterRM.result ?? [];
    const currencies: Currency[] = ccStore.currencyRM.result ?? [];
    const [countries, setCountries] = useState<Country[]>([]);
    const [draft, setDraft] = useState<Draft>(emptyDraft());
    const [currency, setCurrency] = useState({code: '', name: ''});
    const [cluster, setCluster] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const reload = () => {
        ccStore.companyRM.request();
        ccStore.clusterRM.request();
        ccStore.currencyRM.request();
        ccStore.conversionRatesRM.request();
    };
    useEffect(() => {
        // The configuration store loads these once; this page needs their current use counts.
        reload();
        sitApi.listCountries().then(r => setCountries([...r.data].sort((a, b) => a.name.localeCompare(b.name))))
            .catch(() => setError('Could not load the countries.'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // A new company starts from the first cluster, currency and the most used country
    const defaults = (): Draft => emptyDraft(clusters[0]?.id, currencies[0]?.id,
        companies[0]?.country.id ?? countries.find(c => c.code_a3 === 'NLD')?.id ?? countries[0]?.id);
    useEffect(() => {
        if (!draft.id && (!draft.cluster_id || !draft.currency_id || !draft.country_id)) {
            const d = defaults();
            setDraft(x => ({...x, cluster_id: x.cluster_id || d.cluster_id, currency_id: x.currency_id || d.currency_id,
                country_id: x.country_id || d.country_id}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clusters.length, currencies.length, countries.length]);

    const run = (action: () => Promise<unknown>, after?: () => void) => {
        setBusy(true);
        setError(null);
        action().then(() => {
            reload();
            after?.();
        }).catch(e => setError(errorText(e))).finally(() => setBusy(false));
    };

    const saveCompany = () => {
        const {id, ...input} = draft;
        run(() => sitApi.saveCompany({...input, code: input.code.trim(), title: input.title.trim()}, id), () => setDraft(defaults()));
    };
    const addCurrency = () => run(() => sitApi.saveCurrency({code: currency.code.trim().toUpperCase(), name: currency.name.trim()}),
        () => setCurrency({code: '', name: ''}));
    const addCluster = () => run(() => sitApi.saveCluster(cluster.trim()), () => setCluster(''));
    const rename = (label: string, current: string, save: (v: string) => Promise<unknown>) => {
        const value = window.prompt(label, current);
        if (value && value.trim() !== current) run(() => save(value.trim()));
    };
    const remove = (label: string, action: () => Promise<unknown>) => {
        if (window.confirm(`Delete ${label}?`)) run(action);
    };

    const companyReady = draft.code.trim() && draft.title.trim() && draft.cluster_id && draft.currency_id && draft.country_id;
    const missing = [!currencies.length && 'a currency', !clusters.length && 'a cluster'].filter(Boolean);

    return (
        <form className="sit-settings-block" onSubmit={(event) => { event.preventDefault(); if (companyReady) saveCompany(); }}>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {!isAdmin && <Alert severity="info">Only Savings Initiative Tracker admins can change these settings.</Alert>}
            {isAdmin && missing.length > 0 && (
                <Alert severity="info">{`Add ${missing.join(' and ')} first: every company needs one.`}</Alert>
            )}

            <div id="settings-currencies" className="sit-settings-anchor"/>
            <SitSection icon={<Paid/>} title="Currencies" tint="a"
                        sub={<>Initiative financials are entered in these. The reporting currency is chosen in{' '}
                            <Link component="button" type="button" onClick={() => scrollToSettings('general')}>
                                Configuration</Link>; rates between currencies in{' '}
                            <Link component="button" type="button" onClick={() => scrollToSettings('rates')}>
                                Conversion rates</Link>.</>}>
                <div className="sit-conv-table">
                    <table className="sit-table">
                        <thead><tr><th>Code</th><th>Name</th><th>Used by</th>{isAdmin && <th/>}</tr></thead>
                        <tbody>
                            {currencies.map(c => (
                                <tr key={c.id}>
                                    <td><b>{c.code}</b></td>
                                    <td>{c.name}</td>
                                    <td>{usedBy(c.in_use, 'uses')}</td>
                                    {isAdmin && (
                                        <td className="right sit-lever-rowactions">
                                            <button type="button" className="ac-icon-btn" title={`Rename ${c.code}`} disabled={busy}
                                                    onClick={() => rename('Currency name', c.name, v => sitApi.saveCurrency({code: c.code, name: v}, c.id))}>
                                                <EditOutlined fontSize="small"/>
                                            </button>
                                            <button type="button" className="ac-icon-btn" disabled={busy || Boolean(c.in_use)}
                                                    title={c.in_use ? 'In use: it cannot be deleted' : `Delete ${c.code}`}
                                                    onClick={() => remove(c.code, () => sitApi.deleteCurrency(c.id))}>
                                                <DeleteOutline fontSize="small"/>
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                            {currencies.length === 0 && <tr><td colSpan={isAdmin ? 4 : 3} className="sit-muted">No currencies yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
                {isAdmin && (
                    <div className="sit-lever-form sit-company-inline">
                        <SitField label="Code">
                            <input value={currency.code} maxLength={3} placeholder="EUR"
                                   onChange={e => setCurrency({...currency, code: e.target.value.toUpperCase()})}/>
                        </SitField>
                        <SitField label="Name">
                            <input value={currency.name} maxLength={128} placeholder="Euro"
                                   onChange={e => setCurrency({...currency, name: e.target.value})}/>
                        </SitField>
                        <button type="button" className="ac-btn" disabled={busy || currency.code.trim().length !== 3 || !currency.name.trim()}
                                onClick={addCurrency}>
                            <Add fontSize="small"/> Add currency
                        </button>
                    </div>
                )}
            </SitSection>

            <div id="settings-clusters" className="sit-settings-anchor"/>
            <SitSection icon={<Hub/>} title="Clusters" tint="plain"
                        sub="Groups of companies for reporting, e.g. a country or a region.">
                <div className="sit-conv-table">
                    <table className="sit-table">
                        <thead><tr><th>Cluster</th><th>Used by</th>{isAdmin && <th/>}</tr></thead>
                        <tbody>
                            {clusters.map(c => (
                                <tr key={c.id}>
                                    <td><b>{c.title}</b></td>
                                    <td>{usedBy(c.in_use, 'companies or lever links')}</td>
                                    {isAdmin && (
                                        <td className="right sit-lever-rowactions">
                                            <button type="button" className="ac-icon-btn" title={`Rename ${c.title}`} disabled={busy}
                                                    onClick={() => rename('Cluster name', c.title, v => sitApi.saveCluster(v, c.id))}>
                                                <EditOutlined fontSize="small"/>
                                            </button>
                                            <button type="button" className="ac-icon-btn" disabled={busy || Boolean(c.in_use)}
                                                    title={c.in_use ? 'In use: it cannot be deleted' : `Delete ${c.title}`}
                                                    onClick={() => remove(`the cluster "${c.title}"`, () => sitApi.deleteCluster(c.id))}>
                                                <DeleteOutline fontSize="small"/>
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                            {clusters.length === 0 && <tr><td colSpan={isAdmin ? 3 : 2} className="sit-muted">No clusters yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
                {isAdmin && (
                    <div className="sit-lever-form sit-company-inline">
                        <SitField label="New cluster">
                            <input value={cluster} maxLength={128} placeholder="e.g. Netherlands"
                                   onChange={e => setCluster(e.target.value)}/>
                        </SitField>
                        <button type="button" className="ac-btn" disabled={busy || !cluster.trim()} onClick={addCluster}>
                            <Add fontSize="small"/> Add cluster
                        </button>
                    </div>
                )}
            </SitSection>

            <div id="settings-companies" className="sit-settings-anchor"/>
            {isAdmin && (
                <SitSection icon={draft.id ? <EditOutlined/> : <Add/>} tint="a"
                            title={draft.id ? `Edit company ${draft.code}` : 'Add a company'}
                            sub="Initiative financials are split across companies.">
                    <div className="sit-lever-form">
                        <SitField label="Code" req>
                            <input value={draft.code} maxLength={10} placeholder="e.g. NL01"
                                   onChange={e => setDraft({...draft, code: e.target.value})}/>
                        </SitField>
                        <SitField label="Name" req>
                            <input value={draft.title} maxLength={128} placeholder="e.g. Example Holding BV"
                                   onChange={e => setDraft({...draft, title: e.target.value})}/>
                        </SitField>
                        <SitField label="Cluster" req>
                            <select value={draft.cluster_id || ''} onChange={e => setDraft({...draft, cluster_id: Number(e.target.value)})}>
                                {!draft.cluster_id && <option value="">Add a cluster first</option>}
                                {clusters.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                            </select>
                        </SitField>
                        <SitField label="Country" req>
                            <select value={draft.country_id || ''} onChange={e => setDraft({...draft, country_id: Number(e.target.value)})}>
                                {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </SitField>
                        <SitField label="Currency" req>
                            <select value={draft.currency_id || ''} onChange={e => setDraft({...draft, currency_id: Number(e.target.value)})}>
                                {!draft.currency_id && <option value="">Add a currency first</option>}
                                {currencies.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}
                            </select>
                        </SitField>
                        <SitField label="Fiscal year starts in">
                            <select value={draft.fiscal_year_start_month}
                                    onChange={e => setDraft({...draft, fiscal_year_start_month: Number(e.target.value)})}>
                                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                            </select>
                        </SitField>
                    </div>
                    <div className="sit-lever-actions">
                        {draft.id && <button type="button" className="ac-btn" disabled={busy} onClick={() => setDraft(defaults())}>Cancel</button>}
                        <button type="submit" className="ac-btn ac-btn--primary" disabled={!companyReady || busy}>
                            <Check fontSize="small"/> {draft.id ? 'Save company' : 'Add company'}
                        </button>
                    </div>
                </SitSection>
            )}

            <SitSection icon={<Apartment/>} title="Companies" tint="b">
                <div className="sit-conv-table">
                    <table className="sit-table">
                        <thead>
                            <tr>
                                <th>Code</th><th>Name</th><th>Cluster</th><th>Country</th><th>Currency</th>
                                <th>Fiscal year starts</th><th>Used by</th>{isAdmin && <th/>}
                            </tr>
                        </thead>
                        <tbody>
                            {companies.map(c => (
                                <tr key={c.id}>
                                    <td><b>{c.code}</b></td>
                                    <td>{c.title}</td>
                                    <td>{c.cluster.title}</td>
                                    <td>{c.country.name}</td>
                                    <td>{c.currency.code}</td>
                                    <td>{MONTHS[c.fiscal_year_start_month - 1] ?? '—'}</td>
                                    <td>{usedBy(c.in_use, 'initiatives, targets or users')}</td>
                                    {isAdmin && (
                                        <td className="right sit-lever-rowactions">
                                            <button type="button" className="ac-icon-btn" title={`Edit ${c.code}`} disabled={busy}
                                                    onClick={() => setDraft({id: c.id, code: c.code, title: c.title,
                                                        fiscal_year_start_month: c.fiscal_year_start_month, cluster_id: c.cluster.id,
                                                        currency_id: c.currency.id, country_id: c.country.id})}>
                                                <EditOutlined fontSize="small"/>
                                            </button>
                                            <button type="button" className="ac-icon-btn" disabled={busy || Boolean(c.in_use)}
                                                    title={c.in_use ? 'In use: it cannot be deleted' : `Delete ${c.code}`}
                                                    onClick={() => remove(`the company ${c.code}`, () => sitApi.deleteCompany(c.id))}>
                                                <DeleteOutline fontSize="small"/>
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                            {companies.length === 0 && <tr><td colSpan={isAdmin ? 8 : 7} className="sit-muted">No companies yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </SitSection>
        </form>
    );
});
